/**
 * v8.2.5 P1 营业级修复（冒烟 D-03）：收银台购物车状态
 *
 * v8.2.4 的 cart 是 POS.vue 内的局部 ref，而「扫码加购」必须走
 * /pos → /scan-recognize → /pos?product=... 的跨路由往返；router 未启用 keep-alive，
 * POS 每次都会重新挂载，局部 cart 随之初始化 → 第二件商品覆盖第一件。
 *
 * 把购物车提到 pinia（与登录会话同生命周期）后：
 * - 跨路由重新挂载不再丢商品；
 * - 同一商品重复添加沿用了原来的产品口径：累加 quantity，不新增行
 *   （v8.2.4 addToCart 已是 find + quantity++，此处保持不变，未擅自改交互）；
 * - 签退 / 换账号时由 auth 显式清空，下一位收银员不继承上一购物车。
 */
import { defineStore } from 'pinia';
import { ref, computed } from 'vue';

export interface CartItem {
  product_id: string;
  name: string;
  price: number;
  quantity: number;
}

export const useCartStore = defineStore('cart', () => {
  const items = ref<CartItem[]>([]);

  const count = computed(() => items.value.reduce((sum, i) => sum + i.quantity, 0));

  const subtotal = computed(() =>
    items.value.reduce((sum, item) => sum + Number(item.price) * item.quantity, 0)
  );

  /**
   * 加入购物车：已存在同一商品则累加数量，否则追加一行。
   * 价格取本次商品售价（与 v8.2.4 行为一致），不改已存在行的 price。
   */
  function addToCart(product: any) {
    const existing = items.value.find((i) => i.product_id === product.id);
    if (existing) {
      existing.quantity++;
      return existing;
    }
    const row: CartItem = {
      product_id: product.id,
      name: product.name,
      price: Number(product.price),
      quantity: 1,
    };
    items.value.push(row);
    return row;
  }

  function removeByIndex(idx: number) {
    items.value.splice(idx, 1);
  }

  function clear() {
    items.value = [];
  }

  return { items, count, subtotal, addToCart, removeByIndex, clear };
});
